import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Zap, ChevronDown, Activity, Apple, Trophy, LogIn, LayoutDashboard, Ruler, TrendingUp } from "lucide-react";
import { NAV_LINKS, scrollTo } from "@/utils/constants";
import { useAuth } from "@/context/AuthContext";

const TOOL_ITEMS = [
  { name: "Bone Age Prediction", desc: "AI bone age from X-ray", path: "/tools/bone-age/about", icon: Activity, color: "#4ECDC4" },
  { name: "Growth Assessment", desc: "Adult height & GHI", path: "/tools/growth/about", icon: Ruler, color: "#FFE66D" },
  { name: "NIN-NutriGuide", desc: "Nutrition Analysis", path: "/tools/nin-nutriguide/about", icon: Apple, color: "#FF6B6B" },
  { name: "Young Athletes Program", desc: "Pediatric sports-medicine for academies", path: "/young-athletes", icon: Trophy, color: "#FFE66D" },
  { name: "Height Enhancement Program", desc: "Personalised 4-step growth plan", path: "/tools/height-enhancement", icon: TrendingUp, color: "#4ECDC4" },
];

const Navbar = ({ theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef(null);
  const { user } = useAuth();
  const isAuthed = !!user && user.role;

  useEffect(() => {
    const handleClick = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setToolsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleNav = (id) => {
    scrollTo(id);
    setIsOpen(false);
    setToolsOpen(false);
  };

  return (
    <nav className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-5xl" data-testid="navbar">
      <div className="glass-strong rounded-2xl px-6 py-2">
        <div className="flex items-center justify-between">
          <a href="#home" className="flex items-center" data-testid="logo">
            <img
              src="/Tots360_logo.png"
              alt="tots360 - AI-Powered Child Care by Rev & Dev Children Clinic"
              className="h-14 lg:h-16 w-auto object-contain"
              style={{ mixBlendMode: 'screen', filter: 'brightness(1.4)' }}
            />
          </a>

          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((item) =>
              item.id === "tools-section" ? (
                <div key={item.id} className="relative" ref={toolsRef}>
                  <button
                    onClick={() => setToolsOpen((p) => !p)}
                    className="flex items-center gap-1 px-4 py-2 text-sm rounded-xl transition-all text-white/70 hover:text-white hover:bg-white/10"
                    data-testid="nav-tools-dropdown"
                  >
                    {item.label}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${toolsOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {toolsOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-2 w-64 rounded-xl border border-white/15 overflow-hidden shadow-2xl bg-[#0A1029]/95 backdrop-blur-xl"
                        data-testid="tools-dropdown-menu"
                      >
                        <div className="p-2">
                          <button
                            onClick={() => handleNav("tools-section")}
                            className="w-full text-left px-3 py-2 text-xs text-white/40 hover:text-white/60 rounded-lg hover:bg-white/5 transition-colors"
                          >
                            View All Tools
                          </button>
                          {TOOL_ITEMS.map((tool) => (
                            <Link
                              key={tool.path}
                              to={tool.path}
                              onClick={() => setToolsOpen(false)}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/10 transition-colors group"
                              data-testid={`nav-tool-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tool.color}20` }}>
                                <tool.icon className="w-4 h-4" style={{ color: tool.color }} />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white group-hover:text-white">{tool.name}</p>
                                <p className="text-xs text-white/40">{tool.desc}</p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className="px-4 py-2 text-sm rounded-xl transition-all text-white/70 hover:text-white hover:bg-white/10"
                  data-testid={`nav-${item.id}`}
                >
                  {item.label}
                </button>
              )
            )}
          </div>

          <div className="hidden lg:flex items-center gap-2">
            {isAuthed ? (
              <Link
                to="/dashboard"
                className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl transition-all text-white/80 hover:text-white hover:bg-white/10"
                data-testid="nav-dashboard-link"
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl transition-all text-white/80 hover:text-white hover:bg-white/10"
                data-testid="nav-login-link"
              >
                <LogIn className="w-4 h-4" />
                Login
              </Link>
            )}
            <button
              onClick={() => handleNav("contact")}
              className="flex items-center gap-2 btn-glass px-5 py-2.5 rounded-xl text-sm font-medium text-primary"
              data-testid="nav-book-button"
            >
              <Zap className="w-4 h-4 text-[#FFE66D]" />
              Book Screening
            </button>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-button">
              {isOpen ? <X className="w-5 h-5 text-primary" /> : <Menu className="w-5 h-5 text-primary" />}
            </button>
          </div>
        </div>

        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="lg:hidden pt-4 mt-4 border-t border-white/10"
            data-testid="mobile-menu"
          >
            {NAV_LINKS.map((item) =>
              item.id === "tools-section" ? (
                <div key={item.id}>
                  <button
                    onClick={() => handleNav(item.id)}
                    className="block w-full text-left py-2.5 px-3 rounded-lg text-white/80 hover:bg-white/10"
                  >
                    {item.label}
                  </button>
                  <div className="pl-6 space-y-1">
                    {TOOL_ITEMS.map((tool) => (
                      <Link
                        key={tool.path}
                        to={tool.path}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-2 py-2 px-3 rounded-lg text-white/60 hover:text-white hover:bg-white/10 text-sm"
                      >
                        <tool.icon className="w-4 h-4" style={{ color: tool.color }} />
                        {tool.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className="block w-full text-left py-2.5 px-3 rounded-lg text-white/80 hover:bg-white/10"
                >
                  {item.label}
                </button>
              )
            )}
            <button onClick={() => handleNav("contact")} className="w-full mt-3 btn-coral py-3 rounded-xl font-medium">
              Book Screening
            </button>
            {isAuthed ? (
              <Link
                to="/dashboard"
                onClick={() => setIsOpen(false)}
                className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-white/20 text-white/90 hover:bg-white/10"
                data-testid="mobile-nav-dashboard-link"
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                onClick={() => setIsOpen(false)}
                className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-white/20 text-white/90 hover:bg-white/10"
                data-testid="mobile-nav-login-link"
              >
                <LogIn className="w-4 h-4" />
                Login / Sign up
              </Link>
            )}
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
